import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import type { components } from '@/types/apiSchema';
import type { ApiRequestBody } from '@/types/utils/request';

import { chatEndpoints } from './constants';
import { chatQueryKeys } from './queryKeys';
import { refreshJobData } from './refreshJobData';

type StartChatBody = ApiRequestBody<typeof chatEndpoints.chats, 'post'>;
type ChatsPageApi =
  components['schemas']['CursorPageResponseOfAiConversationSummaryResponse'];

/**
 * Starts a chat with its first message; it returns once Finlay has replied, or the reply failed. The chat is listed in
 * the rail meanwhile, under its first message, as finlay-api saves it at once.
 */
export const useStartChatMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: StartChatBody) => {
      const { data } = await fetchClient.POST(chatEndpoints.chats, { body });
      return data;
    },
    onMutate: async ({ id, tenantId, text }) => {
      if (!id) return;
      const queryKey = chatQueryKeys.list(tenantId);
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<InfiniteData<ChatsPageApi | undefined>>(
        queryKey,
        (list) => {
          const [first, ...rest] = list?.pages ?? [];
          if (!list || !first) return list;
          const chat = {
            id,
            title: text,
            lastActivityAt: new Date().toISOString(),
          };
          return {
            ...list,
            pages: [{ ...first, items: [chat, ...first.items] }, ...rest],
          };
        },
      );
    },
    onSuccess: (chat) => {
      if (chat) queryClient.setQueryData(chatQueryKeys.detail(chat.id), chat);
      void refreshJobData(queryClient);
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: chatQueryKeys.lists() }),
  });
};
